import { logError } from "@lib/logger";
import { CustomEventType } from "@/events/CustomEventRegistry";
import { sharedStyles } from '@lib/sharedStyles';
import { LitElement, css, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';

@customElement('millionaire-moderator-panel')
export default class MillionaireModeratorPanel extends LitElement {
	static styles = [
		sharedStyles,
		css`
            .panel {
                box-sizing: border-box;
                border: var(--border-default);
                border-radius: var(--border-radius);
                padding: 12px;
                background: #fff;
                box-shadow: 0 6px 18px rgba(0,0,0,0.06);
                display: flex;
                flex-direction: column;
                gap: 8px;
            }
            .question-item {
                padding: 10px;
                border: 1px solid #eee;
                border-radius: 4px;
                cursor: pointer;
                background: #fdfdfd;
            }
            .question-item:hover {
                background: #f0f8ff;
            }
            .question-item.active {
                background: #e6f0ff;
                border-color: #007bff;
            }
            .controls {
                display: flex;
                gap: 8px;
                flex-wrap: wrap;
                margin-top: 10px;
                padding-top: 10px;
                border-top: 1px solid #eee;
            }
            .top-controls-row {
                display: flex;
                align-items: center;
                justify-content: space-between;
                gap: 8px;
                width: 100%;
            }
            .top-controls-left {
                display: flex;
                gap: 8px;
                flex-wrap: wrap;
            }
            button {
                padding: 8px 12px;
                border: 1px solid #ccc;
                border-radius: 4px;
                cursor: pointer;
                font-family: inherit;
            }
            .btn-primary { background: #007bff; color: white; border-color: #0056b3; }
            .btn-success { background: #28a745; color: white; border-color: #1e7e34; }
            .btn-warning { background: #ffc107; color: black; border-color: #d39e00; }
            .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 4px; margin-top: 4px; }
            .history { margin-top: 16px; }
            .history h4 { margin: 0 0 8px; }
            .history-item {
                padding: 8px 10px;
                border: 1px solid #ddd;
                border-radius: 4px;
                margin-bottom: 6px;
                font-size: 0.9em;
                background: #fafafa;
            }
            .history-item.correct { border-left: 4px solid #28a745; }
            .history-item.wrong   { border-left: 4px solid #dc3545; }
            .history-item.unknown { border-left: 4px solid #aaa; }
            .history-item .q-text { font-weight: 600; margin-bottom: 4px; }
            .history-item .opt { color: #555; font-size: 0.85em; }
            .history-item .opt.selected { font-weight: bold; }
            .history-item .opt.correct-opt { color: #28a745; }
        `
	];

	@state() private loading: boolean = false;
	@state() private selectedQuestion: any | null = null;
	@state() private isAnswerRevealed: boolean = false;
	@state() private lockedOptionId: string | undefined;
	@state() private hasBroadcastedCurrentQuestion: boolean = false;
	@state() private noQuestionsLeft: boolean = false;
	@state() private errorMessage: string | null = null;
	@state() private answeredQuestions: Array<{ question: any; lockedOptionId: string | undefined; wasCorrect: boolean }> = [];

	connectedCallback() {
		super.connectedCallback();
	}

	private _getApiBase() {
		return (window.location.port === '5173') ? 'http://localhost:8002' : '';
	}

	private _shuffleOptions(question: any): any {
		if (!question?.options) return question;
		const shuffled = [...question.options].sort(() => Math.random() - 0.5);
		return { ...question, options: shuffled };
	}

	private async _loadRandomQuestion() {
		this.loading = true;
		this.errorMessage = null;
		try {
			const API_BASE = this._getApiBase();
			const res = await fetch(`${API_BASE}/api/millionaire/questions/random/`);
			if (!res.ok) {
				if (res.status === 404) {
					this.noQuestionsLeft = true;
					this.selectedQuestion = null;
					this.lockedOptionId = undefined;
					this.isAnswerRevealed = false;
					this.hasBroadcastedCurrentQuestion = false;
				}
				return;
			}

			const q = this._shuffleOptions(await res.json());

			// Archive current question before replacing it
			if (this.selectedQuestion) {
				const correctOption = this.selectedQuestion.options?.find((o: any) => o.is_correct);
				this.answeredQuestions = [
					...this.answeredQuestions,
					{
						question: this.selectedQuestion,
						lockedOptionId: this.lockedOptionId,
						wasCorrect: !!this.lockedOptionId && this.lockedOptionId === correctOption?.id,
					}
				];
			}

			this.noQuestionsLeft = false;
			this.selectedQuestion = q;
			this.isAnswerRevealed = false;
			this.lockedOptionId = undefined;
			this.hasBroadcastedCurrentQuestion = false;
		} catch (e) {
			logError(e);
			this.errorMessage = "Frage konnte nicht geladen werden. Bitte erneut versuchen.";
		} finally {
			this.loading = false;
		}
	}

	private async _broadcastQuestion() {
		if (!this.selectedQuestion) return;
		this.hasBroadcastedCurrentQuestion = true;
		this.dispatchEvent(new CustomEvent(CustomEventType.MILLIONAIRE_BROADCAST, {
			detail: {
				action: 'show-question',
				question: this.selectedQuestion,
				showAnswer: false,
				selectedOptionId: undefined
			},
			bubbles: true,
			composed: true
		}));
	}

	private _lockOption(optId: string) {
		this.lockedOptionId = optId;
		this.dispatchEvent(new CustomEvent(CustomEventType.MILLIONAIRE_BROADCAST, {
			detail: {
				action: 'update-state',
				question: this.selectedQuestion, // Re-send question to be safe
				selectedOptionId: optId,
				showAnswer: false
			},
			bubbles: true,
			composed: true
		}));
	}

	private _revealAnswer() {
		this.isAnswerRevealed = true;
		this.dispatchEvent(new CustomEvent(CustomEventType.MILLIONAIRE_BROADCAST, {
			detail: {
				action: 'update-state',
				question: this.selectedQuestion,
				selectedOptionId: this.lockedOptionId,
				showAnswer: true
			},
			bubbles: true,
			composed: true
		}));
	}

	private _refreshPoints() {
		if (!this.hasBroadcastedCurrentQuestion || !this.selectedQuestion) return;

		// Re-send the exact same display state so only the score bar
		// picks up the latest moderator point changes from the event payload.
		this.dispatchEvent(new CustomEvent(CustomEventType.MILLIONAIRE_BROADCAST, {
			detail: {
				action: 'refresh-points',
				question: this.selectedQuestion,
				selectedOptionId: this.lockedOptionId,
				showAnswer: this.isAnswerRevealed
			},
			bubbles: true,
			composed: true
		}));
	}

	private _clear() {
		this.dispatchEvent(new CustomEvent(CustomEventType.MILLIONAIRE_BROADCAST, {
			detail: {
				action: 'clear'
			},
			bubbles: true,
			composed: true
		}));
		this.selectedQuestion = null;
		this.isAnswerRevealed = false;
		this.lockedOptionId = undefined;
		this.hasBroadcastedCurrentQuestion = false;
		this.answeredQuestions = [];
	}

	render() {
		const hasQuestionLoaded = !!this.selectedQuestion;
		const canLoadFirst = !this.loading && !this.noQuestionsLeft && !hasQuestionLoaded;
		const canReload = !this.loading && !this.noQuestionsLeft && hasQuestionLoaded && !this.hasBroadcastedCurrentQuestion;
		const canNext = !this.loading && !this.noQuestionsLeft && hasQuestionLoaded;

		return html`
            <div class="panel">
                <h3>Millionaire Control</h3>
                <div class="controls top-controls-row" style="margin-top:0; padding-top:0; border-top:none;">
                    <div class="top-controls-left">
                        <basic-button ?disabled=${!canLoadFirst} @click=${this._loadRandomQuestion}>Load Question</basic-button>
                        <basic-button ?disabled=${!canReload} @click=${this._loadRandomQuestion}>Reload Question</basic-button>
                        <basic-button ?disabled=${!canNext} @click=${this._loadRandomQuestion}>Next Question</basic-button>
                    </div>
                    <basic-button @click=${this._refreshPoints}>Refresh Points</basic-button>
                </div>

                ${this.loading ? html`<div>Loading...</div>` : nothing}
                ${this.noQuestionsLeft ? html`<div>Keine Fragen mehr vorhanden.</div>` : nothing}
                ${this.errorMessage ? html`<div style="color: #dc3545;">${this.errorMessage}</div>` : nothing}

                ${this.selectedQuestion ? html`
                    <div style="margin-top: 16px; padding: 12px; border: 1px solid #007bff; border-radius: 4px;">
                        <h4>Current Question:</h4>
                        <p>${this.selectedQuestion.text}</p>
                        
                        <div class="controls">
                            <button class="btn-primary" ?disabled=${this.hasBroadcastedCurrentQuestion} @click=${this._broadcastQuestion}>Broadcast Question</button>
                            <button class="btn-success" @click=${this._revealAnswer}>Reveal Answer</button>
                            <button @click=${this._clear}>Clear</button>
                        </div>

                         <div style="margin-top: 8px;">
                            <strong>Lock Answer:</strong>
                            <div class="grid">
                                ${this.selectedQuestion.options.map((opt: any) => html`
                                    <button @click=${() => this._lockOption(opt.id)} 
                                            class="${this.lockedOptionId === opt.id ? 'btn-warning' : ''}"
                                            style="${opt.is_correct ? 'border: 2px solid green;' : ''}"
                                    >
                                        ${opt.text}
                                    </button>
                                `)}
                            </div>
                        </div>
                    </div>
                ` : nothing}

                ${this.answeredQuestions.length > 0 ? html`
                    <div class="history">
                        <h4>Beantwortete Fragen (${this.answeredQuestions.length})</h4>
                        ${[...this.answeredQuestions].reverse().map((entry, reversedIdx) => {
			const num = this.answeredQuestions.length - reversedIdx;
			const statusClass = !entry.lockedOptionId ? 'unknown' : entry.wasCorrect ? 'correct' : 'wrong';
			return html`
                                <div class="history-item ${statusClass}">
                                    <div class="q-text">${num}. ${entry.question.text}</div>
                                    ${entry.question.options?.map((opt: any) => {
				const isSelected = opt.id === entry.lockedOptionId;
				const classes = [
					'opt',
					isSelected ? 'selected' : '',
					opt.is_correct ? 'correct-opt' : ''
				].filter(Boolean).join(' ');
				return html`<div class="${classes}">${isSelected ? '▶ ' : ''}${opt.text}${opt.is_correct ? ' ✓' : ''}</div>`;
			})}
                                </div>
                            `;
		})}
                    </div>
                ` : nothing}
            </div>
        `;
	}
}
